import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, FileText, DollarSign, Clock, CheckCircle, 
  XCircle, AlertCircle, Loader2, ChevronRight, 
  Upload, Eye, Edit, Shield, Wallet, Send
} from 'lucide-react';

// Status badge colors
const statusConfig = {
  draft: { color: 'bg-gray-500/20 text-gray-600', label: 'Draft' },
  active: { color: 'bg-green-500/20 text-green-600', label: 'Active' },
  in_progress: { color: 'bg-blue-500/20 text-blue-600', label: 'In Progress' },
  completed: { color: 'bg-[#3AAFB9]/20 text-[#3AAFB9]', label: 'Completed' },
  cancelled: { color: 'bg-red-500/20 text-red-600', label: 'Cancelled' },
  disputed: { color: 'bg-orange-500/20 text-orange-600', label: 'Disputed' }
};

const paymentStatusConfig = {
  pending: { color: 'bg-yellow-500/20 text-yellow-600', label: 'Pending' },
  escrowed: { color: 'bg-blue-500/20 text-blue-600', label: 'In Escrow' },
  partially_released: { color: 'bg-purple-500/20 text-purple-600', label: 'Partially Released' },
  released: { color: 'bg-green-500/20 text-green-600', label: 'Released' }
};

// Contract Card Component
const ContractCard = ({ contract, onClick, isCreator }) => {
  const status = statusConfig[contract.status] || statusConfig.draft;
  const paymentStatus = paymentStatusConfig[contract.payment_status] || paymentStatusConfig.pending;
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:border-[#3AAFB9]/30"
      onClick={onClick}
      data-testid={`contract-${contract.contract_id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3AAFB9] to-[#3AAFB9]/70 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{contract.campaign_title || 'Contract'}</h3>
              <p className="text-sm text-muted-foreground">
                {isCreator ? `Brand: ${contract.brand_name}` : `Creator: ${contract.creator_name}`}
              </p>
            </div>
          </div>
          <Badge className={status.color}>{status.label}</Badge>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-2 bg-muted/50 rounded-lg">
            <DollarSign className="w-4 h-4 mx-auto text-[#E8795A] mb-1" />
            <p className="text-lg font-bold">${contract.payment_amount}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="text-center p-2 bg-muted/50 rounded-lg">
            <Shield className="w-4 h-4 mx-auto text-[#3AAFB9] mb-1" />
            <p className="text-lg font-bold">${contract.escrow_amount || 0}</p>
            <p className="text-xs text-muted-foreground">Escrow</p>
          </div>
          <div className="text-center p-2 bg-muted/50 rounded-lg">
            <Wallet className="w-4 h-4 mx-auto text-green-500 mb-1" />
            <p className="text-lg font-bold">${contract.released_amount || 0}</p>
            <p className="text-xs text-muted-foreground">Released</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <Badge className={paymentStatus.color}>{paymentStatus.label}</Badge>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            {new Date(contract.created_at).toLocaleDateString()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Deliverable Item Component
const DeliverableItem = ({ deliverable, isCreator, onSubmit, onReview }) => {
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [revisionNotes, setRevisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const statusIcons = {
    pending: <Clock className="w-5 h-5 text-yellow-500" />,
    submitted: <Send className="w-5 h-5 text-blue-500" />,
    revision_requested: <Edit className="w-5 h-5 text-orange-500" />,
    approved: <CheckCircle className="w-5 h-5 text-green-500" />
  };
  
  const handleSubmit = async () => {
    if (!submissionUrl.trim()) {
      toast.error('Please provide a submission URL');
      return;
    }
    setSubmitting(true);
    await onSubmit(deliverable.deliverable_id, submissionUrl, submissionNotes);
    setSubmitting(false);
    setShowSubmitDialog(false);
  };
  
  const handleReview = async (status) => {
    setSubmitting(true);
    await onReview(deliverable.deliverable_id, status, revisionNotes);
    setSubmitting(false);
    setShowReviewDialog(false);
  };
  
  return (
    <div className="p-4 border rounded-xl bg-white/50">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          {statusIcons[deliverable.status] || statusIcons.pending}
          <div>
            <h4 className="font-medium">{deliverable.title}</h4>
            <p className="text-sm text-muted-foreground">{deliverable.description}</p>
            {deliverable.due_date && (
              <p className="text-xs text-muted-foreground mt-1">
                Due: {new Date(deliverable.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="capitalize">{deliverable.status?.replace('_', ' ')}</Badge>
          
          {/* Creator actions */}
          {isCreator && (deliverable.status === 'pending' || deliverable.status === 'revision_requested') && (
            <Button size="sm" onClick={() => setShowSubmitDialog(true)}>
              <Upload className="w-4 h-4 mr-1" />
              Submit
            </Button>
          )}
          
          {/* Brand actions */}
          {!isCreator && deliverable.status === 'submitted' && (
            <Button size="sm" onClick={() => setShowReviewDialog(true)}>
              <Eye className="w-4 h-4 mr-1" />
              Review
            </Button>
          )}
        </div>
      </div>
      
      {/* Submission details */}
      {deliverable.submission_url && (
        <div className="mt-3 p-3 bg-muted/30 rounded-lg">
          <p className="text-sm font-medium mb-1">Submission</p>
          <a 
            href={deliverable.submission_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-[#3AAFB9] hover:underline"
          >
            {deliverable.submission_url}
          </a>
          {deliverable.submission_notes && (
            <p className="text-sm text-muted-foreground mt-1">{deliverable.submission_notes}</p>
          )}
        </div>
      )}
      
      {/* Revision notes */}
      {deliverable.revision_notes && deliverable.status === 'revision_requested' && (
        <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm font-medium text-orange-700 mb-1">Revision Requested</p>
          <p className="text-sm text-orange-600">{deliverable.revision_notes}</p>
          <p className="text-xs text-orange-500 mt-1">Revisions: {deliverable.revision_count || 1}</p>
        </div>
      )}
      
      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Deliverable</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Submission URL *</Label>
              <Input 
                placeholder="https://drive.google.com/..." 
                value={submissionUrl}
                onChange={(e) => setSubmissionUrl(e.target.value)}
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                placeholder="Any additional notes..."
                value={submissionNotes}
                onChange={(e) => setSubmissionNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Deliverable</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {deliverable.submission_url && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium mb-1">Submission</p>
                <a 
                  href={deliverable.submission_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#3AAFB9] hover:underline"
                >
                  View Submission
                </a>
              </div>
            )}
            <div>
              <Label>Revision Notes (if requesting changes)</Label>
              <Textarea 
                placeholder="What changes are needed..."
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>Cancel</Button>
            <Button 
              variant="outline" 
              className="border-orange-500 text-orange-500 hover:bg-orange-50"
              onClick={() => handleReview('revision_requested')}
              disabled={submitting}
            >
              Request Revision
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600"
              onClick={() => handleReview('approved')}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Contracts Page
const Contracts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  const isCreator = user?.role === 'creator';

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? null : activeTab;
      const url = status ? `/contracts/my?status=${status}` : '/contracts/my';
      const response = await api.get(url);
      setContracts(response.data.contracts || []);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const fetchContractDetails = async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}`);
      setSelectedContract(response.data);
    } catch (error) {
      toast.error('Failed to load contract details');
    }
  };

  const handleSignContract = async () => {
    if (!selectedContract) return;
    setActionLoading(true);
    try {
      await api.post(`/contracts/${selectedContract.contract_id}/sign`);
      toast.success('Contract signed successfully!');
      await fetchContractDetails(selectedContract.contract_id);
      fetchContracts();
    } catch (error) {
      toast.error('Failed to sign contract');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFundEscrow = async () => {
    if (!selectedContract) return;
    setActionLoading(true);
    try {
      await api.post(`/contracts/${selectedContract.contract_id}/fund-escrow`);
      toast.success('Escrow funded successfully!');
      await fetchContractDetails(selectedContract.contract_id);
      fetchContracts();
    } catch (error) {
      toast.error('Failed to fund escrow');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDeliverable = async (deliverableId, url, notes) => {
    if (!selectedContract) return;
    try {
      await api.post(`/contracts/${selectedContract.contract_id}/submit-deliverable`, {
        deliverable_id: deliverableId,
        submission_url: url,
        submission_notes: notes
      });
      toast.success('Deliverable submitted!');
      await fetchContractDetails(selectedContract.contract_id);
    } catch (error) {
      toast.error('Failed to submit deliverable');
    }
  };

  const handleReviewDeliverable = async (deliverableId, status, notes) => {
    if (!selectedContract) return;
    try {
      await api.post(`/contracts/${selectedContract.contract_id}/review-deliverable`, {
        deliverable_id: deliverableId,
        status: status,
        revision_notes: notes
      });
      toast.success(status === 'approved' ? 'Deliverable approved!' : 'Revision requested');
      await fetchContractDetails(selectedContract.contract_id);
      fetchContracts();
    } catch (error) {
      toast.error('Failed to review deliverable');
    }
  };

  const handleReleaseMilestone = async (milestoneId) => {
    if (!selectedContract) return;
    setActionLoading(true);
    try {
      await api.post(`/contracts/${selectedContract.contract_id}/release-milestone?milestone_id=${milestoneId}`);
      toast.success('Payment released!');
      await fetchContractDetails(selectedContract.contract_id);
      fetchContracts();
    } catch (error) {
      toast.error('Failed to release payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate progress
  const getProgress = (contract) => {
    if (!contract.deliverables || contract.deliverables.length === 0) return 0;
    const approved = contract.deliverables.filter(d => d.status === 'approved').length;
    return Math.round((approved / contract.deliverables.length) * 100);
  };

  return (
    <div className="min-h-screen dashboard-bg relative overflow-hidden">
      {/* Creative Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Vibrant Orbs */}
        <div className="creator-orb creator-orb-pink" style={{ top: '-5%', right: '10%', opacity: 0.35 }} />
        <div className="creator-orb creator-orb-teal" style={{ bottom: '10%', left: '-5%', opacity: 0.3 }} />
        <div className="creator-orb creator-orb-orange" style={{ top: '40%', right: '-8%', opacity: 0.3 }} />
        
        {/* Network Lines */}
        <div className="network-lines opacity-25" />
      </div>

      {/* Header */}
      <header className="glass-nav sticky top-0 z-40 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">Contracts</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {!selectedContract ? (
          /* Contract List View */
          <>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
              </TabsList>
            </Tabs>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#3AAFB9]" />
              </div>
            ) : contracts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Contracts Yet</h3>
                  <p className="text-muted-foreground">
                    {isCreator 
                      ? 'Contracts will appear here when brands accept your proposals.'
                      : 'Create contracts by accepting creator proposals on your campaigns.'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {contracts.map(contract => (
                  <ContractCard 
                    key={contract.contract_id}
                    contract={contract}
                    isCreator={isCreator}
                    onClick={() => fetchContractDetails(contract.contract_id)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          /* Contract Detail View */
          <div className="space-y-6">
            <Button variant="ghost" onClick={() => setSelectedContract(null)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Contracts
            </Button>

            {/* Contract Header */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl mb-2">{selectedContract.campaign_title}</CardTitle>
                    <p className="text-muted-foreground">
                      Contract ID: {selectedContract.contract_id}
                    </p>
                  </div>
                  <Badge className={statusConfig[selectedContract.status]?.color}>
                    {statusConfig[selectedContract.status]?.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6 mb-6">
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <DollarSign className="w-6 h-6 mx-auto text-[#E8795A] mb-2" />
                    <p className="text-2xl font-bold">${selectedContract.payment_amount}</p>
                    <p className="text-sm text-muted-foreground">Total Payment</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <Shield className="w-6 h-6 mx-auto text-[#3AAFB9] mb-2" />
                    <p className="text-2xl font-bold">${selectedContract.escrow_amount || 0}</p>
                    <p className="text-sm text-muted-foreground">In Escrow</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <Wallet className="w-6 h-6 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">${selectedContract.released_amount || 0}</p>
                    <p className="text-sm text-muted-foreground">Released</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-xl">
                    <CheckCircle className="w-6 h-6 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{getProgress(selectedContract)}%</p>
                    <p className="text-sm text-muted-foreground">Complete</p>
                  </div>
                </div>

                <Progress value={getProgress(selectedContract)} className="h-2 mb-4" />

                {/* Parties */}
                <div className="flex items-center justify-between py-4 border-y">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E8795A]/10 flex items-center justify-center">
                      <span className="font-bold text-[#E8795A]">
                        {selectedContract.creator_name?.charAt(0) || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{selectedContract.creator_name}</p>
                      <p className="text-sm text-muted-foreground">Creator</p>
                    </div>
                    {selectedContract.signed_by_creator && (
                      <Badge className="bg-green-500/20 text-green-600">Signed</Badge>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  <div className="flex items-center gap-3">
                    {selectedContract.signed_by_brand && (
                      <Badge className="bg-green-500/20 text-green-600">Signed</Badge>
                    )}
                    <div className="text-right">
                      <p className="font-medium">{selectedContract.brand_name}</p>
                      <p className="text-sm text-muted-foreground">Brand</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#3AAFB9]/10 flex items-center justify-center">
                      <span className="font-bold text-[#3AAFB9]">
                        {selectedContract.brand_name?.charAt(0) || 'B'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  {/* Sign Contract Button */}
                  {selectedContract.status === 'draft' && (
                    (isCreator && !selectedContract.signed_by_creator) ||
                    (!isCreator && !selectedContract.signed_by_brand)
                  ) && (
                    <Button onClick={handleSignContract} disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      <FileText className="w-4 h-4 mr-2" />
                      Sign Contract
                    </Button>
                  )}

                  {/* Fund Escrow Button (Brand only) */}
                  {!isCreator && 
                   selectedContract.status === 'active' && 
                   selectedContract.payment_status === 'pending' && (
                    <Button onClick={handleFundEscrow} disabled={actionLoading} className="bg-[#3AAFB9]">
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      <Shield className="w-4 h-4 mr-2" />
                      Fund Escrow (${selectedContract.payment_amount})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Terms */}
            {selectedContract.terms && (
              <Card>
                <CardHeader>
                  <CardTitle>Contract Terms</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{selectedContract.terms}</p>
                </CardContent>
              </Card>
            )}

            {/* Deliverables */}
            {selectedContract.deliverables && selectedContract.deliverables.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#3AAFB9]" />
                    Deliverables
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedContract.deliverables.map((deliverable, idx) => (
                    <DeliverableItem 
                      key={deliverable.deliverable_id || idx}
                      deliverable={deliverable}
                      isCreator={isCreator}
                      onSubmit={handleSubmitDeliverable}
                      onReview={handleReviewDeliverable}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Milestones */}
            {selectedContract.milestones && selectedContract.milestones.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#E8795A]" />
                    Payment Milestones
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedContract.milestones.map((milestone, idx) => (
                    <div key={milestone.milestone_id || idx} className="p-4 border rounded-xl bg-white/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{milestone.title}</h4>
                          <p className="text-2xl font-bold text-[#E8795A]">${milestone.payment_amount}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {milestone.status === 'payment_released' ? (
                            <Badge className="bg-green-500/20 text-green-600">Released</Badge>
                          ) : (
                            <>
                              <Badge variant="outline">Pending</Badge>
                              {!isCreator && selectedContract.escrow_amount > 0 && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleReleaseMilestone(milestone.milestone_id)}
                                  disabled={actionLoading}
                                >
                                  Release Payment
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Contracts;
